/**
 * Issues API module (legacy "ticketsApi" name kept for backward compat)
 *
 * Note: ticketsApi and issuesApi both hit the /issues endpoint but return
 * different shapes (PaginatedResponse<Ticket> vs { items: Issue[] }).
 * They are kept as separate objects to preserve backward compatibility.
 */
import type { Ticket, PaginatedResponse } from '@overlayqa/shared';
import type { IssueType } from '@overlayqa/shared';
import { fetchApi, buildQuery } from './client';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';
import { log } from '@/lib/logger';

log.info('[api/tickets] Module loaded');

export const ticketsApi = {
  list: async (token: string | undefined, params?: { projectId?: string; status?: string; severity?: string; type?: string }) => {
    const readPage = (page: number) => {
      const query = buildQuery({
        projectId: params?.projectId,
        status: params?.status,
        severity: params?.severity,
        type: params?.type,
        pageSize: '100',
        page: String(page),
      });
      log.info('[api/tickets] list', { query });
      // Each page retains the screen-read deadline so a stalled continuation
      // reaches the existing load error instead of leaving a partial list.
      return fetchApi<PaginatedResponse<Ticket>>(`/issues${query}`, {
        token,
        timeoutMs: SCREEN_READ_TIMEOUT_MS,
      });
    };
    const first = await readPage(1);
    if (!first.hasMore) return first;

    // useIssuesPageData searches these items locally; neither list consumer
    // provides pagination controls. Finish the list before exposing it to them.
    const items = [...first.items];
    let current = first;
    let page = 1;
    while (current.hasMore) {
      current = await readPage(++page);
      items.push(...current.items);
    }
    return { ...first, items, total: current.total, pageSize: items.length, hasMore: false };
  },

  get: (token: string | undefined, id: string) =>
    fetchApi<Ticket>(`/issues/${id}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  create: (token: string | undefined, data: {
    projectId: string;
    createdById: string;
    title: string;
    description?: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    type: IssueType;
    screenshotUrl: string;
    pageUrl: string;
    viewportWidth?: number;
    viewportHeight?: number;
    devicePixelRatio?: number;
    domSelector?: string;
    figmaFileKey?: string;
    figmaNodeId?: string;
    figmaFrameUrl?: string;
    browserName?: string;
    browserVersion?: string;
    os?: string;
  }) =>
    fetchApi<Ticket>('/issues', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  update: (token: string | undefined, id: string, data: Partial<{
    title: string;
    description: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    type: string;
    status: 'open' | 'in-progress' | 'resolved' | 'verified' | 'closed';
    assigneeId: string | null;
    /** Gesture tag for issue_status_changed; the server never stores it. */
    via: 'not_detected_chip';
    // Fix verification: failed-verification reopen flow sets regressed=true
    // alongside status='open' so the issue list can flag the regression.
    regressed: boolean;
  }>) =>
    fetchApi<Ticket>(`/issues/${id}`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  delete: (token: string | undefined, id: string) =>
    fetchApi<{ deleted: boolean }>(`/issues/${id}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),
};
